/**
 * React, react redux and react localization imports
 */
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
// import { connect } from 'react-redux';

/**
 * Material imports
 */
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  Card as MUICard,
  CardContent as MUICardContent,
  CardMedia as MUICardMedia,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'

import * as Muicon from '@mui/icons-material'
import LockIcon from '@mui/icons-material/Lock'
import { login } from '../../../api/auth'

/**
 * Relative exports to root directory
 */
import { Link } from 'react-router-dom'
import { translateText } from '../../../language'
import { useThemeMode } from '../../../theme'
import { isDefined, isTokenValid } from '../../../utils'
import { resolveLink } from '../../../utils/resolveLink'
import { Button } from './Button'

const stylePrefix = 'CompanionCard'

const getThemeSx = (palette) => ({
  root: {
    width: '100%',
    [`& .${stylePrefix}-avatar-outer`]: {
      overflow: 'hidden',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      '& .MuiAvatar-root': {
        background: palette.headerColor,
        lineHeight: '40px',
        display: 'inline-block'
      }
    },
    [`& .${stylePrefix}-avatar-wrapper`]: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    },
    [`& .${stylePrefix}-avatar-overlay`]: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '4px',
      background: alpha(palette.headerColor, 0.7),
      color: palette.paperColor,
      borderRadius: 0,
      '& .MuiTypography-root': {
        color: palette.paperColor
      }
    },
    [`& .${stylePrefix}-title`]: {
      paddingLeft: '10px',
      color: 'white',
      overflow: 'hidden',
      background: palette.headerColor,
      '& p': {
        fontSize: '0.95rem',
        color: palette.paperColor
      }
    },
    [`& .${stylePrefix}-content p`]: {
      width: '100%',
      overflowWrap: 'break-word',
      wordBreak: 'normal'
    },
    [`& .${stylePrefix}-label`]: {
      color: palette.headerColor
    },
    [`& .${stylePrefix}-icon`]: {
      color: palette.headerColor
    }
  },
  container: {
    outer: { display: 'flex' },
    inner: { display: 'flex', flexDirection: 'column', height: '100%' },
    content: { height: 'calc(100% - 40px)' }
  },
  title: {
    outer: {
      width: '100%',
      minHeight: '30px',
      maxHeight: '60px',
      overflowWrap: 'break-word',
      textAlign: 'left',
      paddingLeft: '10px',
      background: palette.headerColor,
      overflow: 'hidden'
    },
    label: { width: '100%', lineHeight: '30px', color: palette.paperColor }
  },
  media: {
    outer: { width: '30%' },
    component: {
      width: '100%',
      height: '100%',
      '& .img': { width: '30%' }
    }
  },
  actions: {
    component: {
      textAlign: 'right',
      minHeight: 50,
      paddingBottom: '5px',
      boxSizing: 'border-box'
    }
  }
})

export const CardWithAvatar = (props) => {
  const { title, text, src, actions, overlayText } = props
  const { t, i18n } = useTranslation()
  const { palette } = useThemeMode()
  const themeSX = getThemeSx(palette)

  const [imgLoaded, setImgLoaded] = useState(false)
  useEffect(() => { setImgLoaded(false) }, [src])

  const textContent = typeof text === 'string'
    ? <Typography>{text}</Typography>
    : text

  let overlayValue
  if (isDefined(overlayText)) {
    if (Array.isArray(overlayText)) {
      overlayValue = translateText(overlayText, i18n)
    } else if (typeof overlayText === 'string') {
      overlayValue = t(overlayText)
    } else {
      overlayValue = `${overlayText}`
    }
  }

  const hasSrc = isDefined(src)
  const avatarContent = hasSrc
    ? (
      <>
        {!imgLoaded && <CircularProgress size={24} />}
        <img src={src} alt='' onLoad={() => setImgLoaded(true)} style={{ display: 'none' }} />
        {imgLoaded && <Avatar src={src}>{(title ?? text).substring(0, 1)}</Avatar>}
      </>
      )
    : <Avatar>{(title ?? text).substring(0, 1)}</Avatar>

  return (
    <MUICard className={`${stylePrefix}-root`} sx={[themeSX.root, themeSX.container.outer]}>
      <Grid container>
        <Grid item xs={3} md={1} className={`${stylePrefix}-avatar-outer`}>
          <Box className={`${stylePrefix}-avatar-wrapper`}>
            {avatarContent}
            {isDefined(overlayText) && (
              <Box className={`${stylePrefix}-avatar-overlay`}>
                <Typography variant="caption">{overlayValue}</Typography>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={9} md={11}>
          <Grid container>
            <Grid item xs={12} className={`${stylePrefix}-title`}>
              <Typography>{title}</Typography>
            </Grid>
            <Grid item xs={12} className={`${stylePrefix}-content`}>
              <MUICardContent>
                {textContent}
              </MUICardContent>
            </Grid>
            {actions !== undefined && actions !== null && (
              <Grid item xs={12}>
                <Box sx={themeSX.actions.component}>{actions}</Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </MUICard>
  )
}

export const Card = (props) => {
  const {
    title,
    media,
    icon,
    text,
    subtitle,
    actions,
    sx,
    style,
    max,
    fontSize,
    markdown,
    link,
    linkFallback,
    onLinkClick,
    highlightOpenAction,
    className,
    requireAuth,
    linkDisabled
  } = props
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const themeSX = getThemeSx(palette)
  const profile = useSelector((state) => state.auth?.profile)
  const authToken = useSelector((state) => state.auth?.authToken)
  const config = useSelector((state) => state.config)
  const language = useSelector((state) => state.language)

  let cardTitle = ''

  if (title !== undefined) {
    cardTitle = (
      <Box sx={themeSX.title.outer}>
        <Typography sx={themeSX.title.label}>{title}</Typography>
      </Box>
    )
  }

  let cardMedia = ''
  let contentWidth = '100%'

  if (media !== undefined) {
    cardMedia = (
      <Box sx={themeSX.media.outer}>
        <MUICardMedia sx={themeSX.media.component} {...media} />
      </Box>
    )

    contentWidth = '70%'
  }

  if (icon !== undefined && media === undefined) {
    const Icon = Muicon[icon] ? Muicon[icon] : Muicon.NoAccounts

    cardMedia = (
      <Box
        sx={{
          ...themeSX.media.outer,
          textAlign: 'center',
          width: '50px',
          float: 'left'
        }}
      >
        <Icon
          sx={{
            width: '45px',
            height: '45px',
            marginTop: '10px',
            color: palette.headerColor
          }}
        />
      </Box>
    )

    contentWidth = 'calc(100% - 50px)'
  }

  let textContent = ''
  let subtitleContent

  if (subtitle !== undefined) {
    subtitleContent = typeof subtitle === 'string'
      ? <Typography fontSize='0.75rem' opacity={0.8}>{subtitle}</Typography>
      : subtitle
  }

  if (text !== undefined) {
    if (markdown && typeof text === 'string') {
      textContent = (
        <Box
          sx={{
            fontSize: fontSize ?? '0.86rem',
            '& p': { margin: '0 0 0.45em 0', fontSize: 'inherit', lineHeight: 1.35 },
            '& p:last-child': { marginBottom: 0 },
            '& ul, & ol': { margin: '0 0 0.45em 1.2em', padding: 0 },
            '& li': { marginBottom: '0.2em' }
          }}
        >
          <ReactMarkdown>{text}</ReactMarkdown>
        </Box>
      )
    } else if (max !== undefined) {
      textContent = (
        <Typography fontSize={fontSize}>
          {text.substring(0, max - 4)} ...
        </Typography>
      )
    } else {
      textContent = <Typography fontSize={fontSize}>{text}</Typography>
    }
  }

  let linkAction
  const openButtonSx = highlightOpenAction
    ? {
        margin: '5px',
        boxShadow: `0 0 0 1px ${alpha(palette.errorText, 0.5)}, 0 0 10px 2px ${alpha(palette.errorText, 0.55)}`,
        border: `1px solid ${alpha(palette.errorText, 0.75)}`,
        animation: 'companion-open-glow-pulse 1.5s ease-in-out infinite',
        '@keyframes companion-open-glow-pulse': {
          '0%': {
            boxShadow: `0 0 0 1px ${alpha(palette.errorText, 0.45)}, 0 0 8px 1px ${alpha(palette.errorText, 0.4)}`
          },
          '50%': {
            boxShadow: `0 0 0 1px ${alpha(palette.errorText, 0.7)}, 0 0 14px 3px ${alpha(palette.errorText, 0.75)}`
          },
          '100%': {
            boxShadow: `0 0 0 1px ${alpha(palette.errorText, 0.45)}, 0 0 8px 1px ${alpha(palette.errorText, 0.4)}`
          }
        }
      }
    : { margin: '5px' }
  if (linkDisabled && isDefined(link)) {
    linkAction = undefined
  } else if (requireAuth && !(isTokenValid(authToken) && isDefined(profile))) {
    linkAction = (
      <Button
        size="small"
        sx={{ margin: '5px' }}
        startIcon={<LockIcon fontSize="small" />}
        onClick={() => login(config.config, language.active)}
      >
        {t('notification-login-required')}
      </Button>
    )
  } else if (isDefined(link) && link.startsWith('/')) {
    const handleInternalOpen = async (e) => {
      e.preventDefault()
      if (onLinkClick) onLinkClick()
      const resolved = await resolveLink(link, linkFallback)
      if (resolved) {
        window.location.href = resolved
      }
    }
    linkAction = (
      <Link to={link} onClick={handleInternalOpen}>
        <Button size="small" sx={openButtonSx}>Open</Button>
      </Link>
    )
  } else if (isDefined(link)) {
    const handleExternalOpen = async () => {
      if (onLinkClick) onLinkClick()
      const resolved = await resolveLink(link, linkFallback)
      if (resolved) {
        window.open(resolved, '_blank', 'noopener,noreferrer')
      }
    }
    linkAction = (
      <Button
        size="small"
        sx={openButtonSx}
        onClick={handleExternalOpen}
      >
        Open
      </Button>
    )
  }

  let cardActions
  if (actions !== undefined && linkAction !== undefined) {
    cardActions = (
      <Box sx={{ ...themeSX.actions.component, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        {actions}
        {linkAction}
      </Box>
    )
  } else if (actions !== undefined) {
    cardActions = <Box sx={themeSX.actions.component}>{actions}</Box>
  } else if (linkAction !== undefined) {
    cardActions = <Box sx={themeSX.actions.component}>{linkAction}</Box>
  }

  const propsProxy = {
    sx,
    style,
    className
  }

  return (
    <MUICard
      className="companion-card"
      sx={[themeSX.root, themeSX.container.outer]}
      {...propsProxy}
    >
      {cardMedia}
      <Box sx={{ ...themeSX.container.inner, width: contentWidth }}>
        {cardTitle}
        <Box sx={themeSX.container.content}>
          <MUICardContent>
            {subtitleContent}
            {textContent}
            {props.children}
          </MUICardContent>
        </Box>
        {cardActions}
      </Box>
    </MUICard>
  )
}
